import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { FaExclamationCircle, FaCheck } from 'react-icons/fa';

import { UseOutletContextContact } from '../Contact';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { getError } from '../../../../languages/translations/response';
import { ApiDigitalAuthUUID } from '../../../../services/auth/uuid.service';
import { Error } from '../../../../models/error.model';
import { Uuid } from '../../../../models/uuid.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { inputValid, legendIconInvalid, legendIconValid, legendValidInvalidRestart, legendIconValidInvalidRestart, legendInvalid, legendValidRequiredRestart, legendValid } from '../../../../utils/legend.data.util';
import { uploadTooltip } from '../../../../utils/tooltip.util';
import { firstForm, lastForm, nextForm, previousForm, removeFirstForm, removeLastForm } from '../../../../utils/stepper.util';
import { imageCompany } from '../../../../scripts/image.value.script';
import { removeUploadFile, uploadUrl } from '../../../../scripts/file.load.script';
import { evaluateLegendOptional, evaluateLegendOptionalIcon, evaluateLegendRequiredIcon } from '../../../../scripts/validate.legend.script';
import { expressions } from '../../../../libraries/regular.expressions.library';
import { countryName } from '../../../../libraries/country.library';
import { resolveCanton } from '../../../../data/cantons';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';

import AppLegend from '../../../../components/element/Legend';
import AppInputImage from '../../../../components/element/InputImage';
import AppInputCountry from '../../../../components/element/InputCountry';
import AppInputCanton from '../../../../components/element/InputCanton';
import AppInputCurrency from '../../../../components/element/InputCurrency';
import AppInputLanguage from '../../../../components/element/InputLanguage';
import AppInputQrType from '../../../../components/element/InputQrType';
import iconCard from '../../../../assets/themes/icons/card-secret-icon.svg';
import iconBank from '../../../../assets/themes/icons/bank-ico.svg';

export interface AppContactFormStepperProps {
  loadIndicator: string,
  uuid: {value: string, valid: boolean},
  type: {value: string, valid: boolean},
  name: {value: string, valid: boolean},
  firstName: {value: string, valid: boolean},
  lastName: {value: string, valid: boolean},
  logo: {value: {name: string, size: number, type: string, view: string, file: any}, valid: {file: boolean, message: string}},
  email: {value: string, valid: boolean},
  phone: {value: string, valid: boolean},
  website: {value: string, valid: boolean},
  registerNumber: {value: string, valid: boolean},
  vatNumber: {value: string, valid: boolean},
  street: {value: string, valid: boolean},
  streetNumber: {value: string, valid: boolean},
  additional: {value: string, valid: boolean},
  administrativeArea: {value: string, valid: boolean},
  city: {value: string, valid: boolean},
  zipCode: {value: string, valid: boolean},
  country: {value: string, valid: boolean},
  currency: {value: string, valid: boolean, object: any},
  financialEntity: {value: string, valid: boolean},
  iban: {value: string, valid: boolean},
  bban: {value: string, valid: boolean},
  bic: {value: string, valid: boolean},
  language: {value: string, valid: boolean},
  qrType: {value: string, valid: boolean},
  companies: string[],
  setUuid: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setType: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setName: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setFirstName: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setLastName: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setLogo: Dispatch<SetStateAction<{value: {name: string, size: number, type: string, view: string, file: any}, valid: {file: boolean, message: string}}>>,
  setEmail: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setPhone: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setWebsite: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setRegisterNumber: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setVatNumber: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setStreet: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setStreetNumber: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setAdditional: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setAdministrativeArea: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setCity: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setZipCode: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setCountry: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setCurrency: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setFinancialEntity: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setIban: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setBban: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setBic: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setLanguage: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setQrType: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setCompanies: Dispatch<SetStateAction<string[]>>,
  submitContactCreate: Function
};

let errorResponse: Error, uuidResponse: Uuid;

const AppContactFormStepper: React.FunctionComponent<AppContactFormStepperProps> = ({loadIndicator, uuid, type, name, firstName, lastName, logo, email, phone, website, registerNumber, vatNumber, street, streetNumber, additional, administrativeArea, city, zipCode, country, currency, financialEntity, iban, bban, bic, language, qrType, companies, setUuid, setType, setName, setFirstName, setLastName, setLogo, setEmail, setPhone, setWebsite, setRegisterNumber, setVatNumber, setStreet, setStreetNumber, setAdditional, setAdministrativeArea, setCity, setZipCode, setCountry, setCurrency, setFinancialEntity, setIban, setBban, setBic, setLanguage, setQrType, setCompanies, submitContactCreate}) => {
  const {userLoggedIn, appServiceForUser, companyForUser, meCompanies, settingsForCompany} = UseOutletContextContact()
  const {lang} = StateLanguage()

  const [spinnerAnimation, setSpinnerAnimation] = useState(false)
  const [defaultContact, setDefaultContact] = useState<Uuid | null>(null)

  const loadUuid = async (api_key: string, uuid: string) => {
    await ApiDigitalAuthUUID.getUuid(api_key, uuid).then( (response) => {
      if (response.status === 200) {
        uuidResponse = response.data
        setSpinnerAnimation(false)
        setDefaultContact(uuidResponse)
        setType({value: uuidResponse.type, valid: true})

        if (uuidResponse.type === 'person') {
          inputValid('input-first-name')
          inputValid('input-last-name')
          inputValid('input-email')

          setFirstName({value: uuidResponse.data.first_name, valid: uuidResponse.data.first_name.length > 0})
          setLastName({value: uuidResponse.data.last_name, valid: uuidResponse.data.last_name.length > 0})
        }

        if (uuidResponse.type === 'company') {
          inputValid('input-name')

          setName({value: uuidResponse.data.name, valid: uuidResponse.data.name.length > 0})
          setLogo(uploadUrl(lang, uuidResponse.data.logo))
          setRegisterNumber({value: uuidResponse.data.register_number, valid: true})
          setVatNumber({value: uuidResponse.data.vat_number, valid: true})
          setLanguage({value: uuidResponse.data.language, valid: true})
          setQrType({value: uuidResponse.data.qr_type, valid: true})
          setCurrency({value: uuidResponse.data.currency?.code ?? '', object: uuidResponse.data.currency ?? {code: '', name: '', symbol: ''}, valid: true})
          setStreet({value: uuidResponse.data.address.street, valid: true})
          setStreetNumber({value: uuidResponse.data.address.number === 'NN' ? '' : uuidResponse.data.address.number, valid: true})
          setAdditional({value: uuidResponse.data.address.additional, valid: true})
          const rawAdmin = uuidResponse.data.address.administrative_area ?? '';
          const resolvedAdmin = resolveCanton(rawAdmin);
          setAdministrativeArea({value: resolvedAdmin ? resolvedAdmin.code : rawAdmin, valid: true})
          setCity({value: uuidResponse.data.address.city, valid: true})
          setZipCode({value: uuidResponse.data.address.zip_code, valid: true})
          setCountry({value: uuidResponse.data.address.country, valid: true})
        }

        setEmail({value: uuidResponse.data.email, valid: true})
        setPhone({value: uuidResponse.data.phone, valid: true})
        setWebsite({value: uuidResponse.data.website, valid: true})

        uploadTooltip()
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        }).then( () => {
          setSpinnerAnimation(false)
          setDefaultContact(null)
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const handleChangeUuid = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setUuid({...uuid, value: event.target.value})
  }

  const handleChangeType = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setType({value: event.target.value, valid: true})
    legendValid('legend-type-required')
    uploadTooltip()
  }

  const handleChangeName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setName({...name, value: event.target.value})
  }

  const handleChangeFirstName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setFirstName({...firstName, value: event.target.value})
  }

  const handleChangeLastName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setLastName({...lastName, value: event.target.value})
  }

  const handleChangeEmail = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setEmail({...email, value: event.target.value})
  }

  const handleChangePhone = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setPhone({...phone, value: event.target.value})
  }

  const handleChangeWebsite = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setWebsite({...website, value: event.target.value})
  }

  const handleChangeRegisterNumber = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setRegisterNumber({...registerNumber, value: event.target.value})
  }

  const handleChangeVatNumber = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setVatNumber({...vatNumber, value: event.target.value})
  }

  const handleChangeStreet = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setStreet({...street, value: event.target.value})
  }

  const handleChangeStreetNumber = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setStreetNumber({...streetNumber, value: event.target.value})
  }

  const handleChangeAdditional = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setAdditional({...additional, value: event.target.value})
  }

  const handleChangeAdministrativeArea = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setAdministrativeArea({...administrativeArea, value: event.target.value})
  }

  const handleChangeCity = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setCity({...city, value: event.target.value})
  }

  const handleChangeZipCode = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setZipCode({...zipCode, value: event.target.value})
  }

  const handleChangeFinancialEntity = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setFinancialEntity({...financialEntity, value: event.target.value})
  }

  const handleChangeIban = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setIban({...iban, value: event.target.value})
  }

  const handleChangeBban = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setBban({...bban, value: event.target.value})
  }

  const handleChangeBic = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setBic({...bic, value: event.target.value})
  }

  const handleChangeCompanies = (event: React.ChangeEvent <HTMLInputElement>) => {
    if (companies.includes(event.target.value)) {
      setCompanies(companies.filter((temp_item) => (temp_item !== event.target.value)))
    } else {
      setCompanies([...companies, event.target.value])
    }
  }

  const validateUuid = () => {
    if (appServiceForUser && expressions && uuid.value.length > 0) {
      if (expressions.uuid.test(uuid.value)) {
        legendIconValid('input-uuid', 'legend-uuid-valid')
        setUuid({...uuid, valid: true})
        setSpinnerAnimation(true)
        loadUuid(appServiceForUser.api_key, uuid.value)
      } else {
        legendIconInvalid('input-uuid', 'legend-uuid-valid')
        setUuid({...uuid, valid: false})
        setSpinnerAnimation(false)
        setDefaultContact(null)
        uploadData()
        restartForm()
      }
    } else if (uuid.value.length === 0) {
      legendIconValidInvalidRestart('input-uuid', 'legend-uuid-valid')
      setUuid({...uuid, valid: true})
      setSpinnerAnimation(false)
      setDefaultContact(null)
      uploadData()
      restartForm()
    }
  }

  const validateName = () => {
    evaluateLegendRequiredIcon(expressions.name, name, setName, 'input-name', 'legend-name-valid', 'legend-name-required')
  }

  const validateFirstName = () => {
    evaluateLegendRequiredIcon(expressions.name, firstName, setFirstName, 'input-first-name', 'legend-first-name-valid', 'legend-first-name-required')
  }

  const validateLastName = () => {
    evaluateLegendRequiredIcon(expressions.name, lastName, setLastName, 'input-last-name', 'legend-last-name-valid', 'legend-last-name-required')
  }

  const validateEmail = () => {
    evaluateLegendOptionalIcon(expressions.email, email, setEmail, 'input-email', 'legend-email-valid')
  }

  const validatePhone = () => {
    evaluateLegendOptionalIcon(expressions.phone, phone, setPhone, 'input-phone', 'legend-phone-valid')
  }

  const validateWebsite = () => {
    evaluateLegendOptionalIcon(expressions.website, website, setWebsite, 'input-website', 'legend-website-valid')
  }

  const validateRegisterNumber = () => {
    evaluateLegendOptionalIcon(expressions.registernumber, registerNumber, setRegisterNumber, 'input-register-number', 'legend-register-number-valid')
  }

  const validateVatNumber = () => {
    evaluateLegendOptionalIcon(expressions.vat, vatNumber, setVatNumber, 'input-vat-number', 'legend-vat-number-valid')
  }

  const validateStreet = () => {
    evaluateLegendOptionalIcon(expressions.street, street, setStreet, 'input-street', 'legend-street-valid')
  }

  const validateStreetNumber = () => {
    evaluateLegendOptionalIcon(expressions.streetnumber, streetNumber, setStreetNumber, 'input-street-number', 'legend-street-number-valid')
  }

  const validateAdditional = () => {
    evaluateLegendOptionalIcon(expressions.text, additional, setAdditional, 'input-additional', 'legend-additional-valid')
  }

  const validateAdministrativeArea = () => {
    evaluateLegendOptionalIcon(expressions.location, administrativeArea, setAdministrativeArea, 'input-administrative-area', 'legend-administrative-area-valid')
  }

  const validateCity = () => {
    evaluateLegendOptionalIcon(expressions.location, city, setCity, 'input-city', 'legend-city-valid')
  }

  const validateZipCode = () => {
    evaluateLegendOptionalIcon(expressions.zipcode, zipCode, setZipCode, 'input-zip-code', 'legend-zip-code-valid')
  }

  const validateFinancialEntity = () => {
    evaluateLegendOptional(expressions.name, financialEntity, setFinancialEntity, 'legend-financial-entity-valid')
  }

  const validateIban = () => {
    evaluateLegendOptional(expressions.iban, iban, setIban, 'legend-iban-valid')
  }

  const validateBban = () => {
    evaluateLegendOptional(expressions.bban, bban, setBban, 'legend-bban-valid')
  }

  const validateBic = () => {
    evaluateLegendOptional(expressions.bic, bic, setBic, 'legend-bic-valid')
  }

  const executeFormPrevious = () => {
    switch (true) {
      case document.getElementById('form-title-6')?.classList.contains('current'):
        removeLastForm('container-stepper-form')
        previousForm('form-title-5', 'form-title-6', 'form-container-5', 'form-container-6')
        break;
      case document.getElementById('form-title-5')?.classList.contains('current'):
        previousForm('form-title-4', 'form-title-5', 'form-container-4', 'form-container-5')
        break;
      case document.getElementById('form-title-4')?.classList.contains('current'):
        previousForm('form-title-3', 'form-title-4', 'form-container-3', 'form-container-4')
        break;
      case document.getElementById('form-title-3')?.classList.contains('current'):
        previousForm('form-title-2', 'form-title-3', 'form-container-2', 'form-container-3')
        break;
      case document.getElementById('form-title-2')?.classList.contains('current'):
        firstForm('container-stepper-form')
        previousForm('form-title-1', 'form-title-2', 'form-container-1', 'form-container-2')
        break;
    }

    window.scrollTo({top: 0, behavior: 'smooth'})
  }

  const executeFormNext = () => {
    switch (true) {
      case document.getElementById('form-title-1')?.classList.contains('current'):
        if (uuid.valid && type.valid) {
          removeFirstForm('container-stepper-form')
          nextForm('form-title-1', 'form-title-2', 'form-container-1', 'form-container-2')
        } else {
          GlobalSwal.fire({
            text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
            icon: 'error',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            if (!uuid.valid) legendIconInvalid('input-uuid', 'legend-uuid-valid')
            if (!type.valid) legendInvalid('legend-type-required')
          })
        }
        break;
      case document.getElementById('form-title-2')?.classList.contains('current'):
        if (type.value === 'person') {
          if (firstName.valid && lastName.valid) {
            nextForm('form-title-2', 'form-title-3', 'form-container-2', 'form-container-3')
            if (!email.valid) {
              setEmail({value: '', valid: true})
              legendIconValidInvalidRestart('input-email', 'legend-email-valid')
            }
            if (!phone.valid) {
              setPhone({value: '', valid: true})
              legendIconValidInvalidRestart('input-phone', 'legend-phone-valid')
            }
            if (!website.valid) {
              setWebsite({value: '', valid: true})
              legendIconValidInvalidRestart('input-website', 'legend-website-valid')
            }
          } else {
            GlobalSwal.fire({
              text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
              icon: 'error',
              showConfirmButton: false,
              timer: 2200
            }).then( () => {
              if (!firstName.value.length) legendIconInvalid('input-first-name', 'legend-first-name-required')
              if (!lastName.value.length) legendIconInvalid('input-last-name', 'legend-last-name-required')
            })
          }
        }
        if (type.value === 'company') {
          if (name.valid) {
            nextForm('form-title-2', 'form-title-3', 'form-container-2', 'form-container-3')
            if (!logo.valid) {
              setLogo(removeUploadFile)
            }
            if (!email.valid) {
              setEmail({value: '', valid: true})
              legendIconValidInvalidRestart('input-email', 'legend-email-valid')
            }
            if (!phone.valid) {
              setPhone({value: '', valid: true})
              legendIconValidInvalidRestart('input-phone', 'legend-phone-valid')
            }
            if (!website.valid) {
              setWebsite({value: '', valid: true})
              legendIconValidInvalidRestart('input-website', 'legend-website-valid')
            }
            if (!registerNumber.valid) {
              setRegisterNumber({value: '', valid: true})
              legendIconValidInvalidRestart('input-register-number', 'legend-register-number-valid')
            }
            if (!vatNumber.valid) {
              setVatNumber({value: '', valid: true})
              legendIconValidInvalidRestart('input-vat-number', 'legend-vat-number-valid')
            }
          } else {
            GlobalSwal.fire({
              text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
              icon: 'error',
              showConfirmButton: false,
              timer: 2200
            }).then( () => {
              if (!name.value) legendIconInvalid('input-name', 'legend-name-required')
            })
          }
        }
        break;
      case document.getElementById('form-title-3')?.classList.contains('current'):
        nextForm('form-title-3', 'form-title-4', 'form-container-3', 'form-container-4')
        if (!street.valid) {
          setStreet({value: '', valid: true})
          legendIconValidInvalidRestart('input-street', 'legend-street-valid')
        }
        if (!streetNumber.valid) {
          setStreetNumber({value: '', valid: true})
          legendIconValidInvalidRestart('input-street-number', 'legend-street-number-valid')
        }
        if (!additional.valid) {
          setAdditional({value: '', valid: true})
          legendIconValidInvalidRestart('input-additional', 'legend-additional-valid')
        }
        if (!administrativeArea.valid) {
          setAdministrativeArea({value: '', valid: true})
          legendIconValidInvalidRestart('input-administrative-area', 'legend-administrative-area-valid')
        }
        if (!city.valid) {
          setCity({value: '', valid: true})
          legendIconValidInvalidRestart('input-city', 'legend-city-valid')
        }
        if (!zipCode.valid) {
          setZipCode({value: '', valid: true})
          legendIconValidInvalidRestart('input-zip-code', 'legend-zip-code-valid')
        }
        if (!country.valid) {
          setCountry({value: '', valid: true})
        }
        break;
      case document.getElementById('form-title-4')?.classList.contains('current'):
        nextForm('form-title-4', 'form-title-5', 'form-container-4', 'form-container-5')
        if (!financialEntity.valid) {
          setFinancialEntity({...financialEntity, value: '', valid: true})
          legendValidInvalidRestart('legend-financial-entity-valid')
        }
        if (!iban.valid) {
          setIban({value: '', valid: true})
          legendValidInvalidRestart('legend-iban-valid')
        }
        if (!bban.valid) {
          setBban({value: '', valid: true})
          legendValidInvalidRestart('legend-bban-valid')
        }
        if (!bic.valid) {
          setBic({value: '', valid: true})
          legendValidInvalidRestart('legend-bic-valid')
        }
        if (type.value === 'company') {
          if (!currency.valid) {
            setCurrency({value: '', valid: true, object: null})
          }
        }
        break;
      case document.getElementById('form-title-5')?.classList.contains('current'):
        lastForm('container-stepper-form')
        nextForm('form-title-5', 'form-title-6', 'form-container-5', 'form-container-6')
        if (!language.valid) {
          setLanguage({value: '', valid: true})
        }
        if (!qrType.valid) {
          setQrType({value: '', valid: true})
        }
        break;
    }

    window.scrollTo({top: 0, behavior: 'smooth'})
  }

  function restartForm() {
    legendValidInvalidRestart('legend-type-required')
    legendIconValidInvalidRestart('input-name', 'legend-name-valid')
    legendIconValidInvalidRestart('input-name', 'legend-name-required')
    legendIconValidInvalidRestart('input-first-name', 'legend-first-name-valid')
    legendIconValidInvalidRestart('input-first-name', 'legend-first-name-required')
    legendIconValidInvalidRestart('input-last-name', 'legend-last-name-valid')
    legendIconValidInvalidRestart('input-last-name', 'legend-last-name-required')
    legendValidRequiredRestart('legend-logo-type', 'legend-logo-size')
    legendIconValidInvalidRestart('input-email', 'legend-email-valid')
    legendIconValidInvalidRestart('input-phone', 'legend-phone-valid')
    legendIconValidInvalidRestart('input-website', 'legend-website-valid')
    legendIconValidInvalidRestart('input-register-number', 'legend-register-number-valid')
    legendIconValidInvalidRestart('input-vat-number', 'legend-vat-number-valid')
    legendIconValidInvalidRestart('input-street', 'legend-street-valid')
    legendIconValidInvalidRestart('input-street-number', 'legend-street-number-valid')
    legendIconValidInvalidRestart('input-additional', 'legend-additional-valid')
    legendIconValidInvalidRestart('input-administrative-area', 'legend-administrative-area-valid')
    legendIconValidInvalidRestart('input-city', 'legend-city-valid')
    legendIconValidInvalidRestart('input-zip-code', 'legend-zip-code-valid')
    legendValidInvalidRestart('legend-financial-entity-valid')
    legendValidInvalidRestart('legend-iban-valid')
    legendValidInvalidRestart('legend-bban-valid')
    legendValidInvalidRestart('legend-bic-valid')
  }

  function uploadData() {
    if (!companyForUser || !meCompanies) return null

    let company = meCompanies.find((item) => (item.id === companyForUser.id))

    setType({value: '', valid: false})
    setName({value: '', valid: false})
    setFirstName({value: '', valid: false})
    setLastName({value: '', valid: false})
    setEmail({value: '', valid: true})
    setPhone({value: '', valid: true})
    setWebsite({value: '', valid: true})
    setRegisterNumber({value: '', valid: true})
    setVatNumber({value: '', valid: true})
    setStreet({value: '', valid: true})
    setStreetNumber({value: '', valid: true})
    setAdditional({value: '', valid: true})
    setAdministrativeArea({value: '', valid: true})
    setCity({value: '', valid: true})
    setZipCode({value: '', valid: true})
    if (!currency.value && company?.currency) {
      setCurrency({value: company.currency.code ?? '', object: company.currency, valid: true})
    }
    setFinancialEntity({value: '', valid: true})
    setIban({value: '', valid: true})
    setBban({value: '', valid: true})
    setBic({value: '', valid: true})
    if (!language.value && company?.language) {
      setLanguage({value: company.language, valid: true})
    }
    if (!qrType.value && settingsForCompany?.qr_type) {
      setQrType({value: settingsForCompany.qr_type, valid: true})
    }
    setLogo(removeUploadFile())
    setCountry({value: 'CH', valid: true})
  }

  useEffect( () => {
    uploadData()
    uploadTooltip()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser && meCompanies])

  return (
    <>
      { userLoggedIn && companyForUser && meCompanies &&
        <div id="container-stepper-form" className="stepper stepper-pills stepper-column d-flex flex-column flex-xl-row flex-row-fluid">
          <div className="d-flex justify-content-center justify-content-xl-start flex-row-auto w-100 w-xl-300px">
            <div className="stepper-nav">
              <div id="form-title-1" className="stepper-item current">
                <div className="stepper-line w-40px"></div>
                <div className="stepper-icon w-40px h-40px">
                  <i className="stepper-check fas"><FaCheck /></i>
                  <span className="stepper-number">1</span>
                </div>
                <div className="stepper-label w-200px">
                  <h4 className="stepper-title">{lang.labels.uuidAndType}</h4>
                  <div className="stepper-desc">{lang.labels.contactUuidAndType}</div>
                </div>
              </div>
              <div id="form-title-2" className="stepper-item">
                <div className="stepper-line w-40px"></div>
                <div className="stepper-icon w-40px h-40px">
                  <i className="stepper-check"><FaCheck /></i>
                  <span className="stepper-number">2</span>
                </div>
                <div className="stepper-label w-200px">
                  <h4 className="stepper-title">{lang.labels.contactIdentity}</h4>
                  <div className="stepper-desc">{lang.labels.definingTheIdentityOfContact}</div>
                </div>
              </div>
              <div id="form-title-3" className="stepper-item">
                <div className="stepper-line w-40px"></div>
                <div className="stepper-icon w-40px h-40px">
                  <i className="stepper-check"><FaCheck /></i>
                  <span className="stepper-number">3</span>
                </div>
                <div className="stepper-label w-200px">
                  <h4 className="stepper-title">{lang.labels.contactInformation}</h4>
                  <div className="stepper-desc">{lang.labels.definingTheInformationOfContact}</div>
                </div>
              </div>
              <div id="form-title-4" className="stepper-item">
                <div className="stepper-line w-40px"></div>
                <div className="stepper-icon w-40px h-40px">
                  <i className="stepper-check"><FaCheck /></i>
                  <span className="stepper-number">4</span>
                </div>
                <div className="stepper-label w-200px">
                  <h4 className="stepper-title">{lang.labels.legalInformation}</h4>
                  <div className="stepper-desc">{lang.labels.definingTheLegalInformationOfContact}</div>
                </div>
              </div>
              <div id="form-title-5" className="stepper-item">
                <div className="stepper-line w-40px"></div>
                <div className="stepper-icon w-40px h-40px">
                  <i className="stepper-check"><FaCheck /></i>
                  <span className="stepper-number">5</span>
                </div>
                <div className="stepper-label w-200px">
                  <h4 className="stepper-title">{lang.labels.fileDownloadSettings}</h4>
                  <div className="stepper-desc">{lang.labels.definingTheSettingsToDownloadQuotesAndInvoices}</div>
                </div>
              </div>
              <div id="form-title-6" className="stepper-item">
                <div className="stepper-line w-40px"></div>
                <div className="stepper-icon w-40px h-40px">
                  <i className="stepper-check"><FaCheck /></i>
                  <span className="stepper-number">6</span>
                </div>
                <div className="stepper-label w-200px">
                  <h4 className="stepper-title">{lang.labels.company}</h4>
                  <div className="stepper-desc">{lang.labels.selectCompanyAndSave}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex-row-fluid px-lg-5">
            <div className="form">
              <div id="form-container-1" className="current" data-kt-stepper-element="content">
                <div className="w-100">
                  <label className="d-flex fs-6 fw-bold text-justify mb-10">
                    {lang.labels.youCanDirectlyEnterUUIDOfAUserOrCompanyToAddAsContact}
                  </label>
                  <div className="fv-row mb-10">
                    <label className="form-label">
                      <div className="d-flex align-items-center">
                        {lang.labels.contactUUID}
                        <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.specifyTheUniqueUUID}><FaExclamationCircle /></i>
                      </div>
                    </label>
                    <div className="w-100 position-relative">
                      <input id="input-uuid" className="form-control pe-14" type="text" autoComplete="off" name="uuid" value={uuid.value} onChange={handleChangeUuid} onKeyUp={validateUuid} />
                      <span id="icon-spinner" className={`position-absolute top-50 end-0 translate-middle-y me-4 ${!spinnerAnimation && "d-none"}`}>
                        <span className="spinner-border h-15px w-15px align-middle text-muted"></span>
                      </span>
                    </div>
                    <div className="form-text text-justify">{lang.labels.whenCreatingAContactByUUID}</div>
                    <AppLegend attribute={{check: uuid.valid, name: "uuid", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                  </div>
                  <div className="fv-row">
                    <label className="form-label">
                      <div className="d-flex align-items-center">
                        <span className="required">{lang.labels.contactType}</span>
                        <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.selectTheTypeOfContact}><FaExclamationCircle /></i>
                      </div>
                    </label>
                    <div className="fv-row">
                      <label className="d-flex flex-stack mb-5 cursor-pointer">
                        <span className="d-flex align-items-center me-2">
                          <span className="symbol symbol-50px me-5">
                            <span className="symbol-label bg-light-primary">
                              <span className="svg-icon svg-icon-1 svg-icon-primary">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M6.28548 15.0861C7.34369 13.1814 9.35142 12 11.5304 12H12.4696C14.6486 12 16.6563 13.1814 17.7145 15.0861L19.3493 18.0287C20.0899 19.3618 19.1259 21 17.601 21H6.39903C4.87406 21 3.91012 19.3618 4.65071 18.0287L6.28548 15.0861Z" fill="black"/>
                                  <rect opacity="0.3" x="8" y="3" width="8" height="8" rx="4" fill="black"/>
                                </svg>
                              </span>
                            </span>
                          </span>
                          <span className="d-flex flex-column">
                            <span className="fw-bolder">{lang.labels.person}</span>
                            <span className="fs-7 text-muted">{lang.labels.createContactPersonStructure}</span>
                          </span>
                        </span>
                        <span className="form-check form-check-custom form-check-solid">
                          <input id="input-type-person" className="form-check-input" type="radio" name="type-person" value="person" disabled={uuid.value.length > 0} checked={type.value === "person"} onChange={handleChangeType} />
                        </span>
                      </label>
                      <label className="d-flex flex-stack mb-5 cursor-pointer">
                        <span className="d-flex align-items-center me-2">
                          <span className="symbol symbol-50px me-5">
                            <span className="symbol-label bg-light-primary">
                              <span className="svg-icon svg-icon-1 svg-icon-primary">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path opacity="0.3" d="M18 21.6C16.3 21.6 15 20.3 15 18.6V2.50001C15 2.20001 14.6 1.99996 14.3 2.19996L13 3.59999L11.7 2.3C11.3 1.9 10.7 1.9 10.3 2.3L9 3.59999L7.70001 2.3C7.30001 1.9 6.69999 1.9 6.29999 2.3L5 3.59999L3.70001 2.3C3.50001 2.1 3 2.20001 3 3.50001V18.6C3 20.3 4.3 21.6 6 21.6H18Z" fill="black"/>
                                  <path d="M12 12.6H11C10.4 12.6 10 12.2 10 11.6C10 11 10.4 10.6 11 10.6H12C12.6 10.6 13 11 13 11.6C13 12.2 12.6 12.6 12 12.6ZM9 11.6C9 11 8.6 10.6 8 10.6H6C5.4 10.6 5 11 5 11.6C5 12.2 5.4 12.6 6 12.6H8C8.6 12.6 9 12.2 9 11.6ZM9 7.59998C9 6.99998 8.6 6.59998 8 6.59998H6C5.4 6.59998 5 6.99998 5 7.59998C5 8.19998 5.4 8.59998 6 8.59998H8C8.6 8.59998 9 8.19998 9 7.59998ZM13 7.59998C13 6.99998 12.6 6.59998 12 6.59998H11C10.4 6.59998 10 6.99998 10 7.59998C10 8.19998 10.4 8.59998 11 8.59998H12C12.6 8.59998 13 8.19998 13 7.59998ZM13 15.6C13 15 12.6 14.6 12 14.6H10C9.4 14.6 9 15 9 15.6C9 16.2 9.4 16.6 10 16.6H12C12.6 16.6 13 16.2 13 15.6Z" fill="black"/>
                                  <path d="M15 18.6C15 20.3 16.3 21.6 18 21.6C19.7 21.6 21 20.3 21 18.6V12.5C21 12.2 20.6 12 20.3 12.2L19 13.6L17.7 12.3C17.3 11.9 16.7 11.9 16.3 12.3L15 13.6V18.6Z" fill="black"/>
                                </svg>
                                </span>
                            </span>
                          </span>
                          <span className="d-flex flex-column">
                            <span className="fw-bolder">{lang.labels.company}</span>
                            <span className="fs-7 text-muted">{lang.labels.createContactCompanyStructure}</span>
                          </span>
                        </span>
                        <span className="form-check form-check-custom form-check-solid">
                          <input id="input-type-company" className="form-check-input" type="radio" name="type-person" value="company" disabled={uuid.value.length > 0} checked={type.value === "company"} onChange={handleChangeType} />
                        </span>
                      </label>
                    </div>
                    <AppLegend attribute={{check: type.valid, name: "type", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                  </div>
                </div>
              </div>
              <div id="form-container-2" className="pending" data-kt-stepper-element="content">
                <div className="w-100">
                  { type.value === "person" &&
                    <>
                      <div className="fv-row mb-5">
                        <label className="form-label required">{lang.labels.firstName}</label>
                        <input id="input-first-name" className="form-control" type="text" autoComplete="off" name="first-name" disabled={defaultContact?.type === 'person'} value={firstName.value} onChange={handleChangeFirstName} onKeyUp={validateFirstName} onBlur={validateFirstName} />
                        <AppLegend attribute={{check: firstName.valid, name: "first-name", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                      </div>
                      <div className="fv-row mb-5">
                        <label className="form-label required">{lang.labels.lastName}</label>
                        <input id="input-last-name" className="form-control" type="text" autoComplete="off" name="last-name" disabled={defaultContact?.type === 'person'} value={lastName.value} onChange={handleChangeLastName} onKeyUp={validateLastName} onBlur={validateLastName} />
                        <AppLegend attribute={{check: lastName.valid, name: "last-name", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                      </div>
                    </>
                  }
                  { type.value === "company" &&
                    <>
                      <div className="fv-row mb-5">
                        <label className="form-label required">{lang.labels.companyName}</label>
                        <input id="input-name" className="form-control" type="text" autoComplete="off" name="name" disabled={defaultContact?.type === 'company'} value={name.value} onChange={handleChangeName} onKeyUp={validateName} onBlur={validateName} />
                        <AppLegend attribute={{check: name.valid, name: "name", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                      </div>
                      <div className="fv-row mb-5">
                        <label className="form-label">{lang.labels.companyLogo}</label>
                        <div className="mt-5">
                          <AppInputImage image={logo} setImage={setLogo}></AppInputImage>
                        </div>
                      </div>
                    </>
                  }
                  <div className="fv-row mb-5">
                    <label className="form-label">{lang.labels.email}</label>
                    <input id="input-email" className="form-control" type="email" autoComplete="off" name="email" disabled={defaultContact?.type === 'person'} value={email.value} onChange={handleChangeEmail} onKeyUp={validateEmail} onBlur={validateEmail} />
                    <AppLegend attribute={{check: email.valid, name: "email", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                  </div>
                  <div className="fv-row mb-5">
                    <label className="form-label">{lang.labels.phoneNumber}</label>
                    <input id="input-phone" className="form-control" type="tel" autoComplete="off" name="phone" value={phone.value} onChange={handleChangePhone} onKeyUp={validatePhone} onBlur={validatePhone} />
                    <AppLegend attribute={{check: phone.valid, name: "phone", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                  </div>
                  <div className={`fv-row ${type.value === "company" && "mb-5"}`}>
                    <label className="form-label">{lang.labels.webSite}</label>
                    <input id="input-website" className="form-control" type="url" autoComplete="off" name="website" value={website.value} onChange={handleChangeWebsite} onKeyUp={validateWebsite} onBlur={validateWebsite} />
                    <AppLegend attribute={{check: website.valid, name: "website", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                  </div>
                  { type.value === "company" &&
                    <>
                      <div className="fv-row mb-5">
                        <label className="form-label">{lang.labels.registerNumber}</label>
                        <input id="input-register-number" className="form-control" type="text" autoComplete="off" name="register-number" value={registerNumber.value} onChange={handleChangeRegisterNumber} onKeyUp={validateRegisterNumber} onBlur={validateRegisterNumber} />
                        <AppLegend attribute={{check: registerNumber.valid, name: "register-number", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                      </div>
                      <div className="fv-row">
                        <label className="form-label">
                          <div className="d-flex align-items-center">
                            {lang.labels.vatNumber}
                            <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.identificationNumberForACompany}><FaExclamationCircle /></i>
                          </div>
                        </label>
                        <input id="input-vat-number" className="form-control"  type="text" autoComplete="off" name="vat-number" value={vatNumber.value} onChange={handleChangeVatNumber} onKeyUp={validateVatNumber} onBlur={validateVatNumber} />
                        <AppLegend attribute={{check: vatNumber.valid, name: "vat-number", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                      </div>
                    </>
                  }
                </div>
              </div>
              <div id="form-container-3" className="pending" data-kt-stepper-element="content">
                <div className="w-100">
                  <div className="row fv-row mb-5">
                    <div className="col-xl-8 mb-5 mb-xl-0">
                      <label className="form-label">{lang.labels.street}</label>
                      <input id="input-street" className="form-control" type="text" autoComplete="off" name="street" value={street.value} onChange={handleChangeStreet} onKeyUp={validateStreet} onBlur={validateStreet} />
                      <AppLegend attribute={{check: street.valid, name: "street", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                    </div>
                    <div className="col-xl-4">
                      <label className="form-label">
                        <div className="d-flex align-items-center">
                          {lang.labels.number}
                          <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.whenNumberIsNotEntered}><FaExclamationCircle /></i>
                        </div>
                      </label>
                      <input id="input-street-number" className="form-control" type="text" autoComplete="off" name="street-number" placeholder={lang.labels.noNumber} value={streetNumber.value} onChange={handleChangeStreetNumber} onKeyUp={validateStreetNumber} onBlur={validateStreetNumber} />
                      <AppLegend attribute={{check: streetNumber.valid, name: "street-number", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                    </div>
                  </div>
                  <div className="fv-row mb-5">
                    <label className="form-label">
                      <div className="d-flex align-items-center">
                        {lang.labels.additional}
                        <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.ifNecessaryEnterSomethingReferentialRegardingAddress}><FaExclamationCircle /></i>
                      </div>
                    </label>
                    <input id="input-additional" className="form-control" type="text" autoComplete="off" name="additional" value={additional.value} onChange={handleChangeAdditional} onKeyUp={validateAdditional} onBlur={validateAdditional} />
                    <AppLegend attribute={{check: additional.valid, name: "additional", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                  </div>
                  <div className="fv-row mb-5">
                    <label className="form-label">{lang.labels.administrativeArea}</label>
                    {country.value === 'CH'
                      ? <AppInputCanton canton={administrativeArea} setCanton={setAdministrativeArea} options={{disabled: false}} />
                      : <>
                          <input id="input-administrative-area" className="form-control" type="text" autoComplete="off" name="administrative-area" value={administrativeArea.value} onChange={handleChangeAdministrativeArea} onKeyUp={validateAdministrativeArea} onBlur={validateAdministrativeArea} />
                          <AppLegend attribute={{check: administrativeArea.valid, name: "administrative-area", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                        </>
                    }
                  </div>
                  <div className="row fv-row mb-5">
                    <div className="col-xl-4 mb-5 mb-xl-0">
                      <label className="form-label">
                        <div className="d-flex align-items-center">
                          {lang.labels.zipCode}
                          <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.enterThePostalCodeOfTheCity}><FaExclamationCircle /></i>
                        </div>
                      </label>
                      <input id="input-zip-code" className="form-control" type="text" autoComplete="off" name="zip-code" value={zipCode.value} onChange={handleChangeZipCode} onKeyUp={validateZipCode} onBlur={validateZipCode} />
                      <AppLegend attribute={{check: zipCode.valid, name: "zip-code", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                    </div>
                    <div className="col-xl-8">
                      <label className="form-label">{lang.labels.city}</label>
                      <input id="input-city" className="form-control" type="text" autoComplete="off" name="city" value={city.value} onChange={handleChangeCity} onKeyUp={validateCity} onBlur={validateCity} />
                      <AppLegend attribute={{check: city.valid, name: "city", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                    </div>
                  </div>
                  <div className="fv-row">
                    <label className="form-label">{lang.labels.country}</label>
                    <AppInputCountry options={{small: false, disabled: false, placeholder: null}} country={country} setCountry={setCountry}></AppInputCountry>
                  </div>
                </div>
              </div>
              <div id="form-container-4" className="pending" data-kt-stepper-element="content">
                <div className="w-100">
                  { type.value === "company" &&
                    <div className="fv-row mb-5">
                      <label className="form-label">{lang.labels.currency}</label>
                      <AppInputCurrency currency={currency} setCurrency={setCurrency}></AppInputCurrency>
                    </div>
                  }
                  <div className="fv-row mb-5">
                    <label className="form-label">{lang.labels.financialEntity}</label>
                    <div className="position-relative">
                      <input className="form-control pe-13" type="text" autoComplete="off" name="financial-entity" value={financialEntity.value} onChange={handleChangeFinancialEntity} onKeyUp={validateFinancialEntity} onBlur={validateFinancialEntity} />
                      <div className="position-absolute translate-middle-y top-50 end-0 me-3">
                        <img src={iconBank} alt={lang.labels.bank} className="h-20px" />
                      </div>  
                    </div>
                    <AppLegend attribute={{check: financialEntity.valid, name: "financial-entity", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                  </div>
                  <div className="fv-row mb-5">
                    <label className="form-label">
                      <div className="d-flex align-items-center">
                        {lang.labels.iban}
                        <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.internationalBankAccountNumber}><FaExclamationCircle /></i>
                      </div>
                    </label>
                    <div className="position-relative">
                      <input id="input-iban" className="form-control pe-13" type="text" autoComplete="off" name="iban" value={iban.value} onChange={handleChangeIban} onKeyUp={validateIban} onBlur={validateIban} />
                      <div className="position-absolute translate-middle-y top-50 end-0 me-3">
                        <img src={iconCard} alt={lang.labels.card} className="h-20px" />
                      </div>  
                    </div>
                    <AppLegend attribute={{check: iban.valid, name: "iban", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                  </div>
                  <div className="fv-row mb-5">
                    <label className="form-label">
                      <div className="d-flex align-items-center">
                        {lang.labels.bban}
                        <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.basicBankAccountNumber}><FaExclamationCircle /></i>
                      </div>
                    </label>
                    <div className="position-relative">
                      <input id="input-bban" className="form-control pe-13" type="text" autoComplete="off" name="bban" value={bban.value} onChange={handleChangeBban} onKeyUp={validateBban} onBlur={validateBban} />
                      <div className="position-absolute translate-middle-y top-50 end-0 me-3">
                        <img src={iconCard} alt={lang.labels.card} className="h-20px" />
                      </div>
                    </div>
                    <AppLegend attribute={{check: bban.valid, name: "bban", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                  </div>
                  <div className="fv-row">
                    <label className="form-label">
                      <div className="d-flex align-items-center">
                        {lang.labels.bicSwift}
                        <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.bankIdentifierCode}><FaExclamationCircle /></i>
                      </div>
                    </label>
                    <div className="position-relative">
                      <input id="input-bic" className="form-control pe-13" type="text" autoComplete="off" name="bic" value={bic.value} onChange={handleChangeBic} onKeyUp={validateBic} onBlur={validateBic} />
                      <div className="position-absolute translate-middle-y top-50 end-0 me-3">
                        <img src={iconCard} alt={lang.labels.card} className="h-20px" />
                      </div>
                    </div>
                    <AppLegend attribute={{check: bic.valid, name: "bic", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                  </div>
                </div>
              </div>
              <div id="form-container-5" className="pending" data-kt-stepper-element="content">
                <div className="w-100">
                  <label className="fw-bold text-justify mb-10">
                    <span className="fs-6 p-0 me-2">{lang.labels.selectTheOptionsThatBestConsiderWhenDownloading}</span>
                    <span className="text-muted p-0">{lang.labels.byDefaultValuesOfTheCompanyAreTaken}</span>
                  </label>
                  <div className="fv-row mb-5">
                    <label className="form-label">
                      <div className="d-flex align-items-center">
                        {lang.labels.language}
                        <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.languageToDownload}><FaExclamationCircle /></i>
                      </div>
                    </label>
                    <AppInputLanguage language={language} setLanguage={setLanguage}></AppInputLanguage>
                  </div>
                  <div className="fv-row">
                    <label className="form-label">
                      <div className="d-flex align-items-center">
                        {lang.labels.qrType}
                        <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.shapesOrVersionsOfQR}><FaExclamationCircle /></i>
                      </div>
                    </label>
                    <AppInputQrType qrType={qrType} setQrType={setQrType}></AppInputQrType>
                  </div>
                </div>
              </div>
              <div id="form-container-6" className="pending" data-kt-stepper-element="content">
                <div className="w-100">
                  <div className="fv-row">
                    <label className="d-flex align-items-center fs-6 fw-bold text-justify mb-10">
                      {lang.labels.selectCompaniesToCreateContact}
                      <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.listOfCompaniesIHaveAccess}><FaExclamationCircle /></i>
                    </label>
                    <div className="hover-scroll-y">
                      <label className="d-flex flex-stack cursor-pointer my-3">
                        <span className="d-flex align-items-center me-2">
                          <span className="symbol symbol-50px me-5">
                            <span className="symbol-label bg-light">
                              <img src={imageCompany(companyForUser.logo)} alt={lang.labels.logo} className="w-50 h-50" />
                            </span>
                          </span>
                          <span className="d-flex flex-column">
                            <span className="fw-bolder">{companyForUser.name}</span>
                            <span className="fs-7 text-muted">{companyForUser.address?.city}, {countryName(lang.code, companyForUser.address?.country)}</span>
                          </span>
                        </span>
                        <span className="form-check form-check-custom form-check-solid me-2">
                          <input className="form-check-input" type="checkbox" value={companyForUser.id} checked disabled />
                        </span>
                      </label>
                      { meCompanies.map (( (item, index) => { return (
                        <div key={index}>
                          { companyForUser.id !== item.id &&
                            <label className="d-flex flex-stack cursor-pointer my-3">
                              <span className="d-flex align-items-center me-2">
                                <span className="symbol symbol-50px me-5">
                                  <span className="symbol-label bg-light">
                                    <img src={imageCompany(item.logo)} alt={lang.labels.logo} className="w-50 h-50" />
                                  </span>
                                </span>
                                <span className="d-flex flex-column">
                                  <span className="fw-bolder">{item.name}</span>
                                  <span className="fs-7 text-muted">{item.address.city}, {countryName(lang.code, item.address.country)}</span>
                                </span>
                              </span>
                              <span className="form-check form-check-custom form-check-solid me-2">
                                <input className="form-check-input" type="checkbox" value={item.id} checked={companies.includes(item.id)} onChange={handleChangeCompanies} />
                              </span>
                            </label>
                          }
                        </div>
                      )}))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="d-flex flex-stack mt-10">
                <div>
                  <button className="btn btn-light-primary me-3" type="button" data-kt-stepper-action="previous" onClick={executeFormPrevious}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-3 ms-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <rect opacity="0.5" x="6" y="11" width="13" height="2" rx="1" fill="black" />
                          <path d="M8.56569 11.4343L12.75 7.25C13.1642 6.83579 13.1642 6.16421 12.75 5.75C12.3358 5.33579 11.6642 5.33579 11.25 5.75L5.70711 11.2929C5.31658 11.6834 5.31658 12.3166 5.70711 12.7071L11.25 18.25C11.6642 18.6642 12.3358 18.6642 12.75 18.25C13.1642 17.8358 13.1642 17.1642 12.75 16.75L8.56569 12.5657C8.25327 12.2533 8.25327 11.7467 8.56569 11.4343Z" fill="black" />
                        </svg>
                      </span>
                      {lang.labels.back}
                    </div>
                  </button>
                </div>
                <div>
                  <button className="btn btn-primary" type="button" data-kt-stepper-action="next" onClick={executeFormNext}>
                    <div className="d-flex align-items-center">
                      {lang.labels.continue}
                      <span className="svg-icon svg-icon-3 me-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <rect opacity="0.5" x="18" y="13" width="13" height="2" rx="1" transform="rotate(-180 18 13)" fill="black" />
                          <path d="M15.4343 12.5657L11.25 16.75C10.8358 17.1642 10.8358 17.8358 11.25 18.25C11.6642 18.6642 12.3358 18.6642 12.75 18.25L18.2929 12.7071C18.6834 12.3166 18.6834 11.6834 18.2929 11.2929L12.75 5.75C12.3358 5.33579 11.6642 5.33579 11.25 5.75C10.8358 6.16421 10.8358 6.83579 11.25 7.25L15.4343 11.4343C15.7467 11.7467 15.7467 12.2533 15.4343 12.5657Z" fill="black" />
                        </svg>
                      </span>
                    </div>
                  </button>
                  <button className="btn btn-primary" data-kt-stepper-action="submit" type="button" data-kt-indicator={loadIndicator} onClick={() => submitContactCreate()}>
                    <span className="indicator-label">
                      <div className="d-flex align-items-center">
                        {lang.labels.create}
                        <span className="svg-icon svg-icon-3 me-0">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 4L18 12L10 20H14L21.3 12.7C21.7 12.3 21.7 11.7 21.3 11.3L14 4H10Z" fill="black"/>
                            <path opacity="0.3" d="M3 4L11 12L3 20H7L14.3 12.7C14.7 12.3 14.7 11.7 14.3 11.3L7 4H3Z" fill="black"/>
                          </svg>
                        </span>
                      </div>
                    </span>
                    <span className="indicator-progress">
                      {lang.labels.pleaseWait}
                      <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    </>
  )
};

export default AppContactFormStepper;
